import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import { FiLock, FiEye, FiEyeOff, FiCheck } from 'react-icons/fi';

const PasswordField = ({ label, value, onChange, showPassword, onToggleShow }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <FiLock size={15} className="text-gray-400" />
      </div>
      <input
        type={showPassword ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        required
        className="w-full pl-9 pr-10 py-2.5 rounded-lg border border-gray-300 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        placeholder="••••••••"
      />
      <button
        type="button"
        onClick={onToggleShow}
        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
      >
        {showPassword ? <FiEyeOff size={15} /> : <FiEye size={15} />}
      </button>
    </div>
  </div>
);

export default function ChangePassword() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [show, setShow] = useState({ current: false, newPwd: false, confirm: false });
  const [loading, setLoading] = useState(false);

  const toggleShow = (field) => setShow((s) => ({ ...s, [field]: !s[field] }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.newPassword.length < 6) {
      return toast.error('New password must be at least 6 characters');
    }

    if (form.newPassword !== form.confirmPassword) {
      return toast.error('New passwords do not match');
    }

    if (form.currentPassword === form.newPassword) {
      return toast.error('New password must be different from current password');
    }

    setLoading(true);
    try {
      await authService.changePassword(form.currentPassword, form.newPassword);
      toast.success('Password changed successfully');
      navigate('/profile');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">Change Password</h1>
        <p className="text-sm text-gray-500 mt-0.5">Update your account password</p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <form onSubmit={handleSubmit}>
          <div className="p-8 space-y-5">
            <PasswordField label="Current Password" value={form.currentPassword} onChange={(e) => setForm((f) => ({ ...f, currentPassword: e.target.value }))} showPassword={show.current} onToggleShow={() => toggleShow('current')} />
            <PasswordField label="New Password" value={form.newPassword} onChange={(e) => setForm((f) => ({ ...f, newPassword: e.target.value }))} showPassword={show.newPwd} onToggleShow={() => toggleShow('newPwd')} />
            <PasswordField label="Confirm New Password" value={form.confirmPassword} onChange={(e) => setForm((f) => ({ ...f, confirmPassword: e.target.value }))} showPassword={show.confirm} onToggleShow={() => toggleShow('confirm')} />

            {/* Requirements hint */}
            <div className="bg-purple-50 rounded-lg px-4 py-3">
              <p className="text-xs font-semibold text-purple-700 mb-1.5">Password requirements</p>
              <ul className="space-y-1">
                {[
                  ['At least 6 characters', form.newPassword.length >= 6],
                  ['Passwords match', form.newPassword && form.newPassword === form.confirmPassword],
                  ['Different from current', form.currentPassword && form.currentPassword !== form.newPassword],
                ].map(([text, met]) => (
                  <li key={text} className={`flex items-center gap-2 text-xs ${met ? 'text-green-600' : 'text-purple-500'}`}>
                    <FiCheck size={11} className={met ? 'text-green-500' : 'text-purple-300'} />
                    {text}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="px-8 py-5 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate('/profile')}
              className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-60"
            >
              <FiLock size={14} /> {loading ? 'Updating…' : 'Update Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
